import {
  AUTH_TOKEN_COOKIE_KEY,
  LOGIN_STORAGE_KEY,
  RETURN_URL_STORAGE_KEY,
  USER_PRIVILEGES_STORAGE_KEY,
  USER_STORAGE_KEY,
  AGENCY_CONTEXT_STORAGE_KEY,
  LAST_FETCH_STORAGE_KEY,
  USER_CONTEXT_STORAGE_KEY,
  USER_PIC_STORAGE_KEY,
  USER_SIGN_OFF_STORAGE_KEY,
  GOOGLE_AUTH_COOKIE_KEY,
  AGENCY_INFO_STORAGE_KEY,
} from "constants"
import { getCookie, removeCookie } from "utils/cookie"
import { getStorageItem, removeStorageItem, setStorageItem } from "utils/localStorage"
import { LOGIN_PATH } from "constants/app-paths"
import { createPrivilegeObject } from "utils/privileges/privilegesMapper"
import { inDevelopMode } from "utils/env"
import { getAppLanguage } from "./useInitializeApp"
import CryptoJS from "crypto-js"
import store from "store"
import { convertToReadableString } from "utils"
import DateTime from "utils/dateTime"

export const getAppCredentials = () => {
  const token = getCookie(AUTH_TOKEN_COOKIE_KEY) // HTTP Only
  const privileges = getStorageItem(USER_PRIVILEGES_STORAGE_KEY)
  const isLoggedIn = getStorageItem(LOGIN_STORAGE_KEY)
  const info = getStorageItem(USER_STORAGE_KEY)
  const language = getAppLanguage()

  return {
    auth: { token, isLoggedIn: isLoggedIn === "no" ? false : !!isLoggedIn },
    configs: {
      ...language,
      direction: language?.rtl ? "rtl" : "ltr",
      locale: language.key,
      screenSize: { width: window?.innerWidth, height: window?.innerHeight },
    },
    user: { info },
    privileges: privileges ? createPrivilegeObject(privileges) : null,
  }
}

export const removeAppCredentials = (saveUrl, reload) => {
  saveUrl &&
    !inDevelopMode &&
    !window.location.pathname.includes(LOGIN_PATH) &&
    setStorageItem(RETURN_URL_STORAGE_KEY, window.location.href)
  removeCookie(AUTH_TOKEN_COOKIE_KEY)
  removeCookie(GOOGLE_AUTH_COOKIE_KEY)
  removeStorageItem(LOGIN_STORAGE_KEY)
  removeStorageItem(USER_STORAGE_KEY)
  removeStorageItem(USER_PRIVILEGES_STORAGE_KEY)
  removeStorageItem(USER_CONTEXT_STORAGE_KEY)
  removeStorageItem(USER_PIC_STORAGE_KEY)
  removeStorageItem(USER_SIGN_OFF_STORAGE_KEY)
  removeStorageItem(AGENCY_CONTEXT_STORAGE_KEY)
  removeStorageItem(AGENCY_INFO_STORAGE_KEY)
  removeStorageItem(LAST_FETCH_STORAGE_KEY)
  reload && window.location.reload()
}
export const getBrowserName = () => {
  const ua = navigator.userAgent
  if (ua.includes("Opera") || ua.includes("OPR")) {
    return "Opera"
  } else if (ua.includes("Edg")) {
    return "Microsoft Edge"
  } else if (ua.includes("Chrome") && !ua.includes("Chromium")) {
    return "Chrome"
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    return "Safari"
  } else if (ua.includes("Firefox")) {
    return "Firefox"
  } else if (ua.includes("MSIE") || ua.includes("Trident")) {
    return "Internet Explorer"
  } else {
    return false
  }
}

const getUserOS = () => {
  const userAgent = navigator.userAgent
  let userOS = ""
  if (userAgent.includes("Windows")) {
    userOS = "windows"
  } else if (userAgent.includes("Macintosh")) {
    userOS = "mac_os"
  } else if (userAgent.includes("Linux")) {
    userOS = "linux"
  } else {
    userOS = "unknown"
  }
  return userOS
}
const getRandom32BitHash = () => {
  const timestamp = Date.now()
  const combinedInput = Math.random().toString() + timestamp.toString()
  const hash = CryptoJS.SHA256(combinedInput)
  const hashHex = hash.toString(CryptoJS.enc.Hex)
  const hash32Bit = hashHex.slice(0, 32)
  return hash32Bit
}

const simpleHash = (a, b) => {
  const browserName = getBrowserName()
  const combined = browserName?.length ? `${a}${b}${browserName}` : `${a}${b}`
  let hashValue = CryptoJS.SHA256(combined).toString(CryptoJS.enc.Hex)
  return hashValue
}

const getUniqueFingerPrint = uuid => {
  let userUUID = store.getState()?.app?.user?.info?.uuid || uuid
  let fingerprint = store.getState()?.app?.fingerprint
  return simpleHash(fingerprint, userUUID)
}

const getDeviceInfo = (includeEmailHash, uuid) => {
  return {
    deviceInfo: {
      deviceSource: "web",
      deviceOS: getUserOS(),
      appId: process.env.REACT_APP_ID_KEY,
      fingerprint: getUniqueFingerPrint(uuid),
      userAgent: navigator.userAgent,
      timestamp: DateTime.convertDateToString(DateTime.convertDateToUnixTimeStamp(DateTime.currentTime())),
      refToken: getRandom32BitHash(),
      deviceName: `Web - ${convertToReadableString(getUserOS()?.split("_")?.[0])}`, // TODO,
      devicePlatform: getUserOS(),
      ...(store?.getState()?.app?.geoLocation ? { location: store?.getState()?.app?.geoLocation } : {}),
      ...(includeEmailHash ? { verifyViaEmail: true } : {}),
    },
  }
}

export const getDeviceInoHash = (includeEmailHash, uuid) => {
  const deviceInfo = getDeviceInfo(includeEmailHash, uuid).deviceInfo
  const jsonString = JSON.stringify(deviceInfo)
  const key = CryptoJS.enc.Hex.parse(process.env.REACT_APP_DEVICE_INFO_ENCRYPTION_KEY)
  const iv = CryptoJS.enc.Hex.parse(process.env.REACT_APP_DEVICE_INFO_ENCRYPTION_HEX_KEY)
  return CryptoJS.AES.encrypt(jsonString, key, { iv: iv }).toString()
}

export const performEncryption = value => {
  const key = CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPTION_KEY)
  const iv = CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPTION_HEX_KEY)
  return CryptoJS.AES.encrypt(value, key, { iv: iv }).toString()
}
export const performDecryption = value => {
  const key = CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPTION_KEY)
  return CryptoJS.AES.decrypt(value, key, {
    iv: CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPTION_HEX_KEY),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8)
}
